import { useEffect, useState } from 'react';

import { legalInformationServiceUrl } from '../../config/environment';
import { useAsync } from '../../hooks/useAsync';
import { SupportedLocale } from '../../services/locale';
import { callApi } from '../../utils/callApi';
import { makeObservable } from '../../utils/makeObservable';
import { components } from './lims.generated.v3';

export type LegalInformation = {
  imprint: string;
  termsAndConditions: string;
};
type LegalInformationState = { legalInformation?: LegalInformation; loading: boolean; error?: any };
const store = makeObservable<LegalInformationState>();
const legalInformationTenant = 'Hoffmann';

// useLegalInformationService hook
// Example: const { legalInformation, loading, error } = useLegalInformationService('en');
// To update legal-information-fallback.json run next command from the project root,
// the base url is LEGAL_INFORMATION_SERVICE_URL in .env, jq keeps the file prettier compliant:
// curl -sS "$LEGAL_INFORMATION_SERVICE_URL/api/v1/assets?language=en&tenant=Hoffmann" | jq '.' > src/hooks/useLegalInformationService/legal-information-fallback.json
export const useLegalInformationService = (language: string) => {
  const [state, setState] = useState<LegalInformationState>(store.get());
  const { start: load, result, error: loadError } = useAsync(getLegalInformationApiCall);

  useEffect(() => {
    return store.subscribe(setState);
  }, []);

  useEffect(() => {
    if (result) {
      if (result.termsAndConditions && result.imprint) {
        store.set({ legalInformation: result, loading: false });
        return;
      }

      import('./legal-information-fallback.json')
        .then((module) => {
          const fallback = toLegalInformation(module.default);
          const legalInformation = {
            imprint: result.imprint ?? fallback.imprint,
            termsAndConditions: result.termsAndConditions ?? fallback.termsAndConditions,
          };
          store.set({ legalInformation, loading: false });
        })
        .catch((error) => store.set({ loading: false, error }));
    }
  }, [result]);

  useEffect(() => {
    if (loadError) {
      import('./legal-information-fallback.json')
        .then((module) => {
          store.set({ legalInformation: toLegalInformation(module.default), loading: false });
        })
        .catch((error) => store.set({ loading: false, error }));
    }
  }, [loadError]);

  useEffect(() => {
    if (!state) {
      store.set({ loading: true });
      load(language);
    }
  }, [state, language, load]);

  return (
    state || {
      loading: true,
    }
  );
};

type Asset = Omit<components['schemas']['AssetsResponse'], 'type'> & { type: string };
export const getLegalInformationApiCall = async (
  language: string = SupportedLocale.default,
): Promise<LegalInformation> => {
  const url = `${legalInformationServiceUrl}/api/v1/assets?language=${language}&tenant=${legalInformationTenant}`;
  const { data } = await callApi<Asset[]>(url);
  return toLegalInformation(data);
};

const toLegalInformation = (versions: Asset[]): LegalInformation => {
  const map = Object.fromEntries(versions.map((item) => [item.type, item.content]));
  return { termsAndConditions: map.TaC, imprint: map.Imprint };
};
